import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { BookOpen, Package, Settings, LogOut, ChefHat, FileText, LayoutDashboard, Zap, Menu, X, HelpCircle, QrCode, Download } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { getSubscription } from '../api/client'
import InstallAppButton, { useInstallPrompt } from './InstallAppButton'

const nav = [
  { to: '/dashboard',   icon: LayoutDashboard, label: 'Áttekintő' },
  { to: '/receptek',    icon: BookOpen,         label: 'Receptek' },
  { to: '/alapanyagok', icon: Package,          label: 'Alapanyagok' },
  { to: '/haccp',       icon: FileText,         label: 'HACCP' },
  { to: '/nyomonkovetes', icon: QrCode,         label: 'Nyomonkövetés' },
  { to: '/elofizetes',  icon: Zap,              label: 'Előfizetés' },
  { to: '/beallitasok', icon: Settings,         label: 'Beállítások' },
]

const PLAN_LABEL: Record<string, string> = {
  free: 'Ingyenes',
  starter: 'Alap csomag',
  pro: 'Pro csomag',
}

export default function Layout() {
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const { canInstall: canInstallTopBar, promptInstall: promptInstallTopBar } = useInstallPrompt()

  const { data: sub } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => getSubscription().then(r => r.data),
  })

  function logout() {
    localStorage.removeItem('token')
    navigate('/login')
  }

  return (
    <div className="flex h-screen" style={{ background: '#F8F9FA' }}>

      {/* ── Desktop Sidebar ── */}
      <aside className="hidden lg:flex w-60 shrink-0 flex-col" style={{ background: '#1E2D27' }}>
        <div className="px-5 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#5BB8A0' }}>
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-white text-sm leading-tight">CukrászDoc</div>
              <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Gyártmánylap &amp; HACCP</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {nav.map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                  isActive ? 'text-white' : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                }`
              }
              style={({ isActive }) => isActive ? { background: 'rgba(91,184,160,0.18)' } : {}}
            >
              {({ isActive }) => (
                <>
                  <Icon className="w-4 h-4 shrink-0" style={{ color: isActive ? '#5BB8A0' : undefined }} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {sub && (
          <div className="px-3 pb-3">
            <button onClick={() => navigate('/elofizetes')}
              className="w-full rounded-xl px-3 py-3 text-left transition-all hover:opacity-90"
              style={{
                background: sub.plan === 'free' ? 'rgba(255,255,255,0.06)' : 'rgba(91,184,160,0.15)',
                border: sub.plan === 'free' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(91,184,160,0.3)',
              }}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold" style={{ color: sub.plan === 'free' ? 'rgba(255,255,255,0.5)' : '#5BB8A0' }}>
                  {PLAN_LABEL[sub.plan] || sub.plan}
                </span>
                {sub.plan === 'free' && (
                  <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#5BB8A0', color: '#fff' }}>
                    <Zap className="w-2.5 h-2.5" /> Upgrade
                  </span>
                )}
              </div>
              {sub.recipe_limit ? (
                <>
                  <div className="text-[11px] mb-1.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    {sub.recipe_count} / {sub.recipe_limit} recept
                  </div>
                  <div className="h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }}>
                    <div className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, (sub.recipe_count / sub.recipe_limit) * 100)}%`,
                        background: sub.recipe_count >= sub.recipe_limit ? '#f87171' : '#5BB8A0',
                      }} />
                  </div>
                </>
              ) : (
                <div className="text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{sub.recipe_count} recept · korlátlan</div>
              )}
            </button>
          </div>
        )}

        <div className="px-3 pb-5 border-t border-white/10 pt-3 space-y-0.5">
          <InstallAppButton />
          <a href="/kezikonyv.html" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm w-full transition-all duration-150 text-white/40 hover:text-white/70 hover:bg-white/5">
            <HelpCircle className="w-4 h-4" />
            Súgó / Kézikönyv
          </a>
          <button onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm w-full transition-all duration-150 text-white/40 hover:text-white/70 hover:bg-white/5">
            <LogOut className="w-4 h-4" />
            Kijelentkezés
          </button>
        </div>
      </aside>

      {/* ── Mobile overlay menu ── */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMenuOpen(false)} />
          <div className="relative w-72 flex flex-col h-full" style={{ background: '#1E2D27' }}>
            <div className="px-5 py-5 flex items-center justify-between border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#5BB8A0' }}>
                  <ChefHat className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-white">CukrászDoc</span>
              </div>
              <button onClick={() => setMenuOpen(false)} className="text-white/50 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-0.5">
              {nav.map(({ to, icon: Icon, label }) => (
                <NavLink key={to} to={to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium transition-all ${
                      isActive ? 'text-white' : 'text-white/50'
                    }`
                  }
                  style={({ isActive }) => isActive ? { background: 'rgba(91,184,160,0.18)' } : {}}
                >
                  {({ isActive }) => (
                    <>
                      <Icon className="w-5 h-5 shrink-0" style={{ color: isActive ? '#5BB8A0' : undefined }} />
                      {label}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>

            <div className="px-3 pb-6 border-t border-white/10 pt-3 space-y-0.5">
              <InstallAppButton className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm w-full text-white/40 hover:text-white/70" />
              <a href="/kezikonyv.html" target="_blank" rel="noopener noreferrer"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm w-full text-white/40 hover:text-white/70">
                <HelpCircle className="w-5 h-5" />
                Súgó / Kézikönyv
              </a>
              <button onClick={logout}
                className="flex items-center gap-3 px-3 py-3 rounded-xl text-sm w-full text-white/40 hover:text-white/70">
                <LogOut className="w-5 h-5" />
                Kijelentkezés
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Mobile top bar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#5BB8A0' }}>
              <ChefHat className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-sm" style={{ color: '#1E2D27' }}>CukrászDoc</span>
          </div>
          <div className="flex items-center gap-1.5">
            {canInstallTopBar && (
              <button onClick={promptInstallTopBar}
                className="px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-semibold whitespace-nowrap"
                style={{ background: 'rgba(91,184,160,0.15)', color: '#5BB8A0' }}>
                <Download className="w-3.5 h-3.5" />
                Alkalmazás telepítése
              </button>
            )}
            <button onClick={() => setMenuOpen(true)} className="p-2 rounded-lg" style={{ color: '#1E2D27' }}>
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Telepítési banner — mobilon a top bar alatt is, hogy biztosan feltűnjön */}
        {canInstallTopBar && (
          <button
            onClick={promptInstallTopBar}
            className="lg:hidden w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold shrink-0"
            style={{ background: '#5BB8A0', color: '#fff' }}
          >
            <Download className="w-4 h-4" />
            Alkalmazás telepítése a kezdőképernyőre
          </button>
        )}

        <main className="flex-1 overflow-auto pb-16 lg:pb-0">
          <div className="p-4 lg:p-7 min-h-full">
            <Outlet />
          </div>
        </main>

        {/* Mobile bottom navigation */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex z-40">
          {nav.slice(0, 5).map(({ to, icon: Icon, label }) => (
            <NavLink key={to} to={to}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition-colors ${
                  isActive ? '' : 'text-gray-400'
                }`
              }
              style={({ isActive }) => isActive ? { color: '#5BB8A0' } : {}}
            >
              {({ isActive }) => (
                <>
                  <Icon className="w-5 h-5" style={{ color: isActive ? '#5BB8A0' : undefined }} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
          <button
            onClick={() => setMenuOpen(true)}
            className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium text-gray-400"
          >
            <Menu className="w-5 h-5" />
            Több
          </button>
        </nav>
      </div>
    </div>
  )
}
